import React, { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import SearchIcon from "@mui/icons-material/Search";
import {
  useTheme,
  Box,
  Card,
  Button,
  Divider,
  Typography,
  CardContent,
  useMediaQuery,
  Tooltip,
  FormControlLabel,
  Switch,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  Checkbox,
} from '@mui/material';
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { parseISO, format } from 'date-fns';
import Title from "../../components/Title";
import { Office } from '../../models/office';
import HeaderResponsive from '../../components/layouts/HeaderResponsive';
import SimpleCustomTable from '../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, RowId } from '../../components/table/interface';
import { formatCurrency } from '../../utils/format/currency';
import { minYmd, nowOperationalDateString } from "../../utils/operationalDateTime";
import { useDrawer } from '../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../components/layouts/StickyListToolbar';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface PrincipalExpenseOption {
  id: number;
  name: string;
  type_operation_name: string;
}

interface MainBoxMovementRow {
  id: number;
  date: string;
  expense_name: string;
  description: string;
  type_operation_name: string;
  value: number;
  balance_after: number;
}

const MovimientoCaja: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [office_id, setOffice_id] = useState("");
  const current_date = nowOperationalDateString();
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [movements, setMovements] = useState<MainBoxMovementRow[]>([]);
  const [principalExpenses, setPrincipalExpenses] = useState<PrincipalExpenseOption[]>([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [selectAllExpenses, setSelectAllExpenses] = useState(false);
  const { drawerOpen } = useDrawer();
  const [soloSinCobro, setSoloSinCobro] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ data: PrincipalExpenseOption[] }>("/report/mainboxmovementexpenses");
        setPrincipalExpenses(data.data ?? []);
      } catch {
        setPrincipalExpenses([]);
      }
    })();
  }, []);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!office_id || !start_date || !end_date) {
      showAlert('Seleccione oficina y rango de fechas.', 'warning');
      return;
    }

    const formData = {
      end_date,
      start_date,
      office_id,
      expense_ids: selectedExpenseIds.map((id) => Number(id)),
      solo_sin_cobro: soloSinCobro,
    };

    showLoading();
    try {
      const { data } = await axios.post<{ data: MainBoxMovementRow[] }>("/report/mainboxmovements", formData);
      const list = data.data ?? [];
      setMovements(list);
      if (list.length === 0) {
        showAlert(
          'No hay movimientos en el periodo. Pruebe otras fechas, oficina, detalles de operación o «Solo movimientos sin cobro».',
          'info',
        );
      }
    } catch (error: unknown) {
      console.error("Error:", error);
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No fue posible cargar el informe.';
      showAlert(msg, 'error');
      setMovements([]);
    } finally {
      hideLoading();
    }
  };

  const handleSelectAllExpenses = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedExpenseIds(principalExpenses.map((e) => String(e.id)));
      setSelectAllExpenses(true);
    } else {
      setSelectedExpenseIds([]);
      setSelectAllExpenses(false);
    }
  };

  const handleExpenseCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedExpenseIds((prev) => {
      if (prev.includes(value)) {
        return prev.filter((id) => id !== value);
      }
      return [...prev, value];
    });
  };

  useEffect(() => {
    if (principalExpenses.length === 0) {
      setSelectAllExpenses(false);
      return;
    }
    setSelectAllExpenses(
      selectedExpenseIds.length > 0 && selectedExpenseIds.length === principalExpenses.length,
    );
  }, [selectedExpenseIds, principalExpenses.length]);

  const [offices, setOffices] = useState<Office[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ data: Office[] }>("offices");
        setOffices(data.data ?? []);
      } catch {
        setOffices([]);
      }
    })();
  }, []);

  const formatDateCell = (value: string | undefined) => {
    if (!value) return '-';
    try {
      const s = String(value);
      const iso = s.length <= 10 ? `${s}T12:00:00` : s;
      return format(parseISO(iso), 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return String(value);
    }
  };

  const expenseCheckboxIndeterminate =
    selectedExpenseIds.length > 0 && selectedExpenseIds.length < principalExpenses.length;

  const columns: SimpleColumn[] = [
    {
      field: 'date',
      headerName: 'Fecha',
      minWidth: 170,
      renderCell: ({ row }) => formatDateCell(row.date as string),
    },
    {
      field: 'expense_name',
      headerName: 'Detalle operación',
      minWidth: 200,
      wrapText: true,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      minWidth: 200,
      wrapText: true,
    },
    {
      field: 'type_operation_name',
      headerName: 'Operación',
      minWidth: 150,
      wrapText: true,
    },
    {
      field: 'value',
      headerName: 'Valor',
      align: 'right',
      minWidth: 130,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.value ?? 0))}</Typography>
      ),
    },
    {
      field: 'balance_after',
      headerName: 'Saldo mov.',
      align: 'right',
      minWidth: 130,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.balance_after ?? 0))}</Typography>
      ),
    },
  ];

  const rows: SimpleRow[] = movements.map((m) => ({
    id: m.id as RowId,
    date: m.date,
    expense_name: m.expense_name,
    description: m.description,
    type_operation_name: m.type_operation_name,
    value: m.value,
    balance_after: m.balance_after,
  }));

  return (
    <HeaderResponsive border={false}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        mb={2}
      >
        <Box>
          <Title>Movimientos Caja Principal</Title>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Solo movimientos de <strong>gasto de oficina</strong> en caja principal (tipo Gasto(s) Caja Principal). No incluye traslados entre caja principal y cajas de cobro. Filtre por <strong>detalle de operación</strong>; si no marca ninguno, se incluyen todos los ítems de ese tipo.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={submit}
          startIcon={isMobile ? null : <SearchIcon />}
          sx={{ flexShrink: 0 }}
        >
          {isMobile ? <SearchIcon /> : "Buscar"}
        </Button>
      </Box>
      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
        }}
      >
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={3}>
              <TextField
                fullWidth
                label="Fecha Inicio"
                type="date"
                size="small"
                value={start_date}
                onChange={(e) => setStart_date(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: minYmd(end_date || nowOperationalDateString(), nowOperationalDateString()) }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <TextField
                fullWidth
                label="Fecha Fin"
                type="date"
                size="small"
                value={end_date}
                onChange={(e) => setEnd_date(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: start_date, max: nowOperationalDateString() }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Oficina</InputLabel>
                <Select
                  label="Oficina"
                  value={office_id}
                  onChange={(e) => setOffice_id(e.target.value)}
                >
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.id.toString()}>{office.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormControl sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                <InputLabel shrink>Marcar gastos</InputLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectAllExpenses}
                      onChange={handleSelectAllExpenses}
                      disabled={principalExpenses.length === 0}
                      indeterminate={expenseCheckboxIndeterminate}
                    />
                  }
                  label="Todos los detalles"
                />
              </FormControl>
            </Grid>
          </Grid>

          {principalExpenses.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Detalle de operación (gasto caja principal)
              </Typography>
              <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 0.5 }}>
                {principalExpenses.map((exp) => {
                  const op = exp.type_operation_name ? ` (${exp.type_operation_name})` : '';
                  return (
                    <FormControlLabel
                      key={exp.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedExpenseIds.includes(String(exp.id))}
                          onChange={handleExpenseCheckboxChange}
                          value={String(exp.id)}
                        />
                      }
                      label={`${exp.name}${op}`}
                      sx={{ mr: 2, maxWidth: 320 }}
                    />
                  );
                })}
              </FormGroup>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No hay gastos de caja principal activos en catálogo para filtrar.
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Tooltip title="Solo movimientos donde ni el origen ni el destino son una caja de cobro (solo caja principal de oficina u operaciones sin otra caja con cobro).">
              <FormControlLabel
                control={
                  <Switch
                    checked={soloSinCobro}
                    onChange={(_, checked) => setSoloSinCobro(checked)}
                    color="primary"
                  />
                }
                label="Solo movimientos sin cobro (caja oficina)"
              />
            </Tooltip>
          </Box>
          <Divider sx={{ my: 4 }} />
          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={columns}
              maxHeight={600}
              showBorders
              showCount
              exportCsv
              exportFileName="movimientos-caja-principal.csv"
              wrapCellContent
            />
          ) : null}
          <Divider sx={{ my: 4 }} />
        </CardContent>
      </Card>
    </HeaderResponsive >
  );
};

export default MovimientoCaja;
